import { Controller } from '@hotwired/stimulus'
import '../../entrypoints/modules/semantic-ui-dropdown'
import 'semantic-ui-transition/transition.min.js'
import 'semantic-ui-transition/transition.min.css'
import 'semantic-ui-api/api.js'
import 'semantic-ui-search/search.min.js'
import { trans, TIMING_ON_DAY } from '../../translator'

export default class extends Controller {
  static targets = ['template', 'markerSelect', 'groupSelect', 'list', 'noMarkersMessage', 'content', 'markersSource', 'addButton']
  static values = { isRepeatMarkers: { type: Boolean, default: true }, numberOfDays: { type: Number, default: 1 } }

  // Per-day marker distinctions (day suffixes, day-namespaced ids) only make sense for a
  // non-repeating log that actually spans more than one day.
  get isMultiDay () {
    return !this.isRepeatMarkersValue && this.numberOfDaysValue > 1
  }

  connect () {
    this.initializeRows()

    this.markerAddedHandler = this.handleMarkerAdded.bind(this)
    this.markerRemovedHandler = this.handleMarkerRemoved.bind(this)
    this.allDaysModeUpdatedHandler = this.handleAllDaysModeUpdated.bind(this)
    this.dateRangeUpdatedHandler = this.handleDateUpdated.bind(this)
    document.addEventListener('markers:marker:added', this.markerAddedHandler)
    document.addEventListener('markers:marker:removed', this.markerRemovedHandler)
    document.addEventListener('markers:all-days-mode:updated', this.allDaysModeUpdatedHandler)
    document.addEventListener('markers:date-range:updated', this.dateRangeUpdatedHandler)
  }

  disconnect () {
    document.removeEventListener('markers:marker:added', this.markerAddedHandler)
    document.removeEventListener('markers:marker:removed', this.markerRemovedHandler)
    document.removeEventListener('markers:all-days-mode:updated', this.allDaysModeUpdatedHandler)
    document.removeEventListener('markers:date-range:updated', this.dateRangeUpdatedHandler)
  }

  initializeRows () {
    this.getRows().forEach(row => this.initializeRow(row))
    this.refreshMarkerDropdowns()
    this.updateAddButtonVisibility()
  }

  initializeRow (row) {
    const markerDropdown = row.querySelector('[data-log-event-marker-notifications-target="markerSelect"]')
    const groupSelect = row.querySelector('[data-log-event-marker-notifications-target="groupSelect"]')

    this.initializeMarkerDropdown(row, markerDropdown)
    this.initializeGroupDropdown(groupSelect)

    const selectedMarkerId = this.getDropdownValue(markerDropdown)
    if (selectedMarkerId) {
      row.dataset.logMarkerId = selectedMarkerId
      this.syncRowMarkerMetadata(row, selectedMarkerId)
    }
  }

  addEventMarkerNotification () {
    if (!this.hasListTarget || !this.hasTemplateTarget) {
      return
    }

    if (this.getRows().length >= this.getAllMarkers().length) {
      return
    }

    const fragment = this.templateTarget.content.cloneNode(true)
    this.listTarget.appendChild(fragment)

    const notificationRow = this.listTarget.lastElementChild
    if (!notificationRow) {
      return
    }

    this.initializeRow(notificationRow)
    this.refreshMarkerDropdowns(notificationRow)
    this.updateAddButtonVisibility()
  }

  removeRow (event) {
    const notificationRow = event.currentTarget.closest('[data-notification-row]')
    if (notificationRow) {
      notificationRow.remove()
      this.refreshMarkerDropdowns()
      this.updateAddButtonVisibility()
    }
  }

  initializeMarkerDropdown (row, dropdown) {
    if (!dropdown) {
      return
    }

    $(dropdown).dropdown({
      onChange: (value, text) => {
        row.dataset.logMarkerId = value || ''
        this.updateMarkerDropdownText(dropdown, text)
        this.refreshMarkerDropdowns(row)
      }
    })
  }

  initializeGroupDropdown (dropdown) {
    if (!dropdown) {
      return
    }

    $(dropdown).dropdown({
      onAdd: (addedValue) => this.handleGroupAdd(dropdown, addedValue)
    })
  }

  handleGroupAdd (dropdown, addedValue) {
    if (!addedValue) {
      return
    }

    const values = this.getDropdownValues(dropdown)
    if (addedValue === 'ALL') {
      const selectedValues = values.filter(value => value !== 'ALL')
      setTimeout(() => {
        selectedValues.forEach(value => this.removeDropdownSelection(dropdown, value))
      }, 0)
      return
    }

    if (values.includes('ALL')) {
      setTimeout(() => {
        this.removeDropdownSelection(dropdown, 'ALL')
      }, 0)
    }
  }

  removeDropdownSelection (dropdown, value) {
    if (!dropdown || !value) {
      return
    }

    $(dropdown).dropdown('remove selected', value)
  }

  getRows () {
    if (!this.hasListTarget) {
      return []
    }

    return Array.from(this.listTarget.querySelectorAll('[data-notification-row]'))
  }

  getAllMarkers () {
    if (!this.hasMarkersSourceTarget) {
      return []
    }

    return Array.from(this.markersSourceTarget.querySelectorAll('[data-log-marker-option]')).map((marker) => ({
      id: marker.dataset.logMarkerId,
      name: marker.dataset.logMarkerName
    }))
  }

  getSelectedMarkerIds (excludedRow = null) {
    return this.getRows()
      .filter(row => row !== excludedRow)
      .map(row => row.dataset.logMarkerId)
      .filter(Boolean)
  }

  getAvailableMarkers (excludedRow = null) {
    const selectedMarkerIds = new Set(this.getSelectedMarkerIds(excludedRow))
    return this.getAllMarkers().filter(marker => !selectedMarkerIds.has(marker.id))
  }

  refreshMarkerDropdowns (changedRow = null) {
    this.getRows().forEach((row) => {
      const markerDropdown = row.querySelector('[data-log-event-marker-notifications-target="markerSelect"]')
      const selectedMarkerId = row.dataset.logMarkerId || this.getDropdownValue(markerDropdown)
      const selectedMarker = this.getAllMarkers().find(marker => marker.id === selectedMarkerId)
      const availableMarkers = this.getAvailableMarkers(row)

      if (selectedMarker && !availableMarkers.some(marker => marker.id === selectedMarker.id)) {
        availableMarkers.push(selectedMarker)
      }

      this.populateMarkerOptions(markerDropdown, availableMarkers)

      if (selectedMarker) {
        this.setMarkerDropdownSelection(markerDropdown, selectedMarker.id, selectedMarker.name)
        row.dataset.logMarkerId = selectedMarker.id
        this.syncRowMarkerMetadata(row, selectedMarker.id)
        return
      }

      if (changedRow === row && availableMarkers.length === 1) {
        this.setMarkerDropdownSelection(markerDropdown, availableMarkers[0].id, availableMarkers[0].name)
        row.dataset.logMarkerId = availableMarkers[0].id
        this.syncRowMarkerMetadata(row, availableMarkers[0].id)
        return
      }

      this.clearMarkerDropdownSelection(markerDropdown)
      delete row.dataset.logMarkerId
      delete row.dataset.logMarkerBaseId
      delete row.dataset.logMarkerDay
    })

    this.updateAddButtonVisibility()
  }

  populateMarkerOptions (dropdown, markers) {
    if (!dropdown) {
      return
    }

    const menu = dropdown.querySelector('.menu')
    if (!menu) {
      return
    }

    menu.replaceChildren()

    markers.forEach(({ id, name }) => {
      const item = document.createElement('div')
      item.className = 'item'
      item.dataset.value = id
      item.textContent = name
      menu.appendChild(item)
    })

    $(dropdown).dropdown('refresh')
  }

  setMarkerDropdownSelection (dropdown, value, text) {
    if (!dropdown || !value) {
      return
    }

    $(dropdown).dropdown('set selected', value)
    $(dropdown).dropdown('set text', text)

    const textElement = dropdown.querySelector('.text')
    if (textElement) {
      textElement.textContent = text
    }
  }

  clearMarkerDropdownSelection (dropdown) {
    if (!dropdown) {
      return
    }

    $(dropdown).dropdown('clear')

    const textElement = dropdown.querySelector('.text')
    if (textElement) {
      textElement.textContent = dropdown.querySelector('.default.text')?.textContent || ''
    }
  }

  updateMarkerDropdownText (dropdown, text) {
    if (!dropdown || !text) {
      return
    }

    const textElement = dropdown.querySelector('.text')
    if (textElement) {
      textElement.textContent = text
    }
  }

  getDropdownValue (dropdown) {
    if (!dropdown) {
      return ''
    }

    return $(dropdown).dropdown('get value')
  }

  getDropdownValues (dropdown) {
    const value = this.getDropdownValue(dropdown)
    if (Array.isArray(value)) {
      return value.filter(Boolean)
    }

    if (typeof value === 'string') {
      return value.split(',').filter(Boolean)
    }

    return []
  }

  updateAddButtonVisibility () {
    if (!this.hasAddButtonTarget) {
      return
    }

    this.addButtonTarget.classList.toggle('d-none', this.getRows().length >= this.getAllMarkers().length)
  }

  getMarkerOptionById (id) {
    if (!this.hasMarkersSourceTarget || !id) {
      return null
    }

    return this.markersSourceTarget.querySelector(`[data-log-marker-option][data-log-marker-id="${id}"]`)
  }

  syncRowMarkerMetadata (row, markerId) {
    if (!row) {
      return
    }

    const markerOption = this.getMarkerOptionById(markerId)
    if (!markerOption) {
      delete row.dataset.logMarkerBaseId
      delete row.dataset.logMarkerDay
      return
    }

    if (markerOption.dataset.logMarkerBaseId) {
      row.dataset.logMarkerBaseId = markerOption.dataset.logMarkerBaseId
    } else {
      delete row.dataset.logMarkerBaseId
    }

    if (markerOption.dataset.logMarkerDay !== undefined) {
      row.dataset.logMarkerDay = markerOption.dataset.logMarkerDay
    } else {
      delete row.dataset.logMarkerDay
    }
  }

  resolveMarkerOptionContext ({ logMarkerId, markerId, day }) {
    const id = logMarkerId || markerId
    if (!id) {
      return null
    }

    const parsedDay = Number.parseInt(day, 10)
    if (!this.isMultiDay || Number.isNaN(parsedDay)) {
      return { id }
    }

    const existingOption = this.markersSourceTarget.querySelector(`[data-log-marker-option][data-log-marker-id="${id}"][data-log-marker-day="${parsedDay}"]`) ||
      this.markersSourceTarget.querySelector(`[data-log-marker-option][data-log-marker-base-id="${id}"][data-log-marker-day="${parsedDay}"]`)

    if (existingOption) {
      return {
        id: existingOption.dataset.logMarkerId,
        baseId: existingOption.dataset.logMarkerBaseId,
        baseName: existingOption.dataset.logMarkerBaseName,
        day: parsedDay
      }
    }

    return {
      id: `${id}-day-${parsedDay}`,
      baseId: logMarkerId || id,
      day: parsedDay
    }
  }

  upsertMarkerOption (id, name, metadata = {}) {
    if (!this.hasMarkersSourceTarget || !id || !name) {
      return
    }

    const existingMarker = this.getMarkerOptionById(id)
    if (existingMarker) {
      existingMarker.dataset.logMarkerName = name
      if (metadata.baseId) {
        existingMarker.dataset.logMarkerBaseId = metadata.baseId
      }
      if (metadata.baseName) {
        existingMarker.dataset.logMarkerBaseName = metadata.baseName
      }
      if (metadata.day !== undefined) {
        existingMarker.dataset.logMarkerDay = metadata.day
      }
      return
    }

    const markerOption = document.createElement('div')
    markerOption.dataset.logMarkerOption = ''
    markerOption.dataset.logMarkerId = id
    markerOption.dataset.logMarkerName = name
    if (metadata.baseId) {
      markerOption.dataset.logMarkerBaseId = metadata.baseId
    }
    if (metadata.baseName) {
      markerOption.dataset.logMarkerBaseName = metadata.baseName
    }
    if (metadata.day !== undefined) {
      markerOption.dataset.logMarkerDay = metadata.day
    }
    this.markersSourceTarget.appendChild(markerOption)
  }

  removeMarkerOption (id) {
    if (!this.hasMarkersSourceTarget || !id) {
      return
    }

    const markerOption = this.getMarkerOptionById(id)
    if (markerOption) {
      markerOption.remove()
    }
  }

  handleMarkerAdded (event) {
    const { logMarkerId, markerId, markerName, day } = event.detail
    const context = this.resolveMarkerOptionContext({ logMarkerId, markerId, day })
    if (!context || !markerName) {
      return
    }

    const displayName = this.isMultiDay
      ? trans(TIMING_ON_DAY, { '%timing-name%': markerName, '%timing-day%': parseInt(day ?? 0) + 1 }, 'logs')
      : markerName

    if (this.hasNoMarkersMessageTarget) {
      this.noMarkersMessageTarget.classList.add('d-none')
    }
    if (this.hasContentTarget) {
      this.contentTarget.classList.remove('d-none')
    }

    this.upsertMarkerOption(context.id, displayName, {
      baseId: context.baseId,
      baseName: context.baseName || markerName,
      day: context.day
    })
    this.refreshMarkerDropdowns()
  }

  captureNotificationRowState () {
    return this.getRows()
      .map(row => {
        if (!row.dataset.logMarkerId) {
          return null
        }
        const groupSelect = row.querySelector('[data-log-event-marker-notifications-target="groupSelect"]')
        const selectedValues = Array.from(groupSelect.querySelectorAll('option:checked')).map(o => o.value)
        return {
          markerId: row.dataset.logMarkerId,
          baseMarkerId: row.dataset.logMarkerBaseId || row.dataset.logMarkerId,
          selectedGroups: selectedValues.filter(v => v !== 'ALL'),
          notifyAll: selectedValues.includes('ALL')
        }
      })
      .filter(Boolean)
  }

  appendNotificationRow (markerId, baseMarkerId, groupIds, notifyAll) {
    if (!this.hasTemplateTarget || !this.hasListTarget) {
      return
    }
    this.listTarget.appendChild(this.templateTarget.content.cloneNode(true))
    const row = this.listTarget.lastElementChild
    if (!row) {
      return
    }

    row.dataset.logMarkerId = markerId
    if (baseMarkerId && baseMarkerId !== markerId) {
      row.dataset.logMarkerBaseId = baseMarkerId
    }

    const hiddenInput = row.querySelector('input[type="hidden"]')
    if (hiddenInput) {
      hiddenInput.value = markerId
    }

    const groupSelect = row.querySelector('[data-log-event-marker-notifications-target="groupSelect"]')
    if (groupSelect) {
      Array.from(groupSelect.options).forEach(option => {
        option.selected = (option.value === 'ALL' && notifyAll) || groupIds.includes(option.value)
      })
    }

    this.initializeRow(row)
  }

  // Collapses per-day markers/notification rows back to one entry per unique base marker,
  // used both when the log switches to repeating markers and when it shrinks to a single day.
  collapseToUniqueMarkers (capturedStates) {
    const seen = new Set()
    const unique = []
    for (const entry of this.markersSourceTarget.querySelectorAll('[data-log-marker-option]')) {
      const baseId = entry.dataset.logMarkerBaseId || entry.dataset.logMarkerId
      if (!seen.has(baseId)) {
        seen.add(baseId)
        unique.push({ id: baseId, name: entry.dataset.logMarkerBaseName || entry.dataset.logMarkerName })
      }
    }
    this.markersSourceTarget.replaceChildren()
    unique.forEach(({ id, name }) => {
      const div = document.createElement('div')
      div.dataset.logMarkerOption = ''
      div.dataset.logMarkerId = id
      div.dataset.logMarkerName = name
      this.markersSourceTarget.appendChild(div)
    })

    this.listTarget.replaceChildren()
    const seenBase = new Set()
    capturedStates.forEach(state => {
      if (!seenBase.has(state.baseMarkerId)) {
        seenBase.add(state.baseMarkerId)
        this.appendNotificationRow(state.baseMarkerId, null, state.selectedGroups, state.notifyAll)
      }
    })
  }

  // Fans one collapsed entry per marker out into one entry per marker per day, rebuilding the
  // notification rows to match. Expects markersSource to hold collapsed (day-less) entries.
  expandToPerDayMarkers (numberOfDays, capturedStates) {
    const entries = Array.from(this.markersSourceTarget.querySelectorAll('[data-log-marker-option]'))
    this.markersSourceTarget.replaceChildren()
    for (let day = 0; day < numberOfDays; day++) {
      entries.forEach(entry => {
        const div = document.createElement('div')
        div.dataset.logMarkerOption = ''
        div.dataset.logMarkerId = `${entry.dataset.logMarkerId}-day-${day}`
        div.dataset.logMarkerBaseId = entry.dataset.logMarkerId
        div.dataset.logMarkerBaseName = entry.dataset.logMarkerName
        div.dataset.logMarkerDay = day
        div.dataset.logMarkerName = `${entry.dataset.logMarkerName} – Day ${day + 1}`
        this.markersSourceTarget.appendChild(div)
      })
    }

    this.listTarget.replaceChildren()
    for (let day = 0; day < numberOfDays; day++) {
      capturedStates.forEach(state => {
        this.appendNotificationRow(`${state.baseMarkerId}-day-${day}`, state.baseMarkerId, state.selectedGroups, state.notifyAll)
      })
    }
  }

  handleAllDaysModeUpdated (event) {
    const { isApplyToAllDays, numberOfDays } = event.detail
    const capturedStates = this.captureNotificationRowState()

    if (numberOfDays !== undefined) {
      this.numberOfDaysValue = numberOfDays
    }

    if (isApplyToAllDays) {
      this.collapseToUniqueMarkers(capturedStates)
    } else {
      this.expandToPerDayMarkers(numberOfDays, capturedStates)
    }

    this.isRepeatMarkersValue = isApplyToAllDays
    this.refreshMarkerDropdowns()
    this.updateAddButtonVisibility()
  }

  handleDateUpdated (event) {
    if (this.isRepeatMarkersValue) {
      return
    }
    const numberOfDays = parseInt(event.detail.numberOfDays)
    if (!numberOfDays || numberOfDays < 1) {
      return
    }
    this.numberOfDaysValue = numberOfDays

    if (numberOfDays <= 1) {
      this.collapseToUniqueMarkers(this.captureNotificationRowState())
      this.refreshMarkerDropdowns()
      this.updateAddButtonVisibility()
      return
    }

    // An earlier collapse to a single day left one day-less entry per marker. Those have to be
    // fanned back out, or the per-day loop below would add its entries alongside them.
    if (this.markersSourceTarget.querySelector('[data-log-marker-option]:not([data-log-marker-day])') &&
        !this.markersSourceTarget.querySelector('[data-log-marker-option][data-log-marker-day]')) {
      this.expandToPerDayMarkers(numberOfDays, this.captureNotificationRowState())
      this.refreshMarkerDropdowns()
      this.updateAddButtonVisibility()
      return
    }

    const outOfRangeIds = new Set()
    for (const entry of Array.from(this.markersSourceTarget.querySelectorAll('[data-log-marker-option]'))) {
      const day = entry.dataset.logMarkerDay
      if (day !== undefined && parseInt(day) >= numberOfDays) {
        outOfRangeIds.add(entry.dataset.logMarkerId)
        entry.remove()
      }
    }

    if (outOfRangeIds.size > 0) {
      this.getRows()
        .filter(row => outOfRangeIds.has(row.dataset.logMarkerId))
        .forEach(row => row.remove())
    }

    const existingEntries = Array.from(this.markersSourceTarget.querySelectorAll('[data-log-marker-option]'))
    const existingDays = new Set(existingEntries.map(e => e.dataset.logMarkerDay).filter(Boolean))

    const seenBases = new Set()
    const baseMarkers = []
    for (const entry of existingEntries) {
      const baseId = entry.dataset.logMarkerBaseId || entry.dataset.logMarkerId
      const baseName = entry.dataset.logMarkerBaseName || entry.dataset.logMarkerName
      if (!seenBases.has(baseId)) {
        seenBases.add(baseId)
        baseMarkers.push({ baseId, baseName })
      }
    }

    let addedAny = false
    for (let day = 0; day < numberOfDays; day++) {
      if (!existingDays.has(String(day))) {
        baseMarkers.forEach(({ baseId, baseName }) => {
          const div = document.createElement('div')
          div.dataset.logMarkerOption = ''
          div.dataset.logMarkerId = `${baseId}-day-${day}`
          div.dataset.logMarkerBaseId = baseId
          div.dataset.logMarkerBaseName = baseName
          div.dataset.logMarkerDay = day
          div.dataset.logMarkerName = `${baseName} – Day ${day + 1}`
          this.markersSourceTarget.appendChild(div)
        })
        addedAny = true
      }
    }

    if (outOfRangeIds.size === 0 && !addedAny) {
      return
    }

    this.refreshMarkerDropdowns()
    this.updateAddButtonVisibility()
  }

  handleMarkerRemoved (event) {
    const { logMarkerId, markerId, isMarkerRowRemoved, day } = event.detail
    if (!isMarkerRowRemoved) {
      return
    }

    const context = this.resolveMarkerOptionContext({ logMarkerId, markerId, day })
    const id = context?.id
    if (!id) return

    this.removeMarkerOption(id)

    this.getRows()
      .filter(row => row.dataset.logMarkerId === id)
      .forEach(row => row.remove())

    this.refreshMarkerDropdowns()

    if (this.hasNoMarkersMessageTarget && this.hasContentTarget) {
      if (this.getAllMarkers().length === 0) {
        this.noMarkersMessageTarget.classList.remove('d-none')
        this.contentTarget.classList.add('d-none')
      }
    }
  }
}
